import copy from 'copy-to-clipboard'
import { find } from 'lodash'
import { Key, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useKey } from 'react-use'
import { Item, Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'
import { Menu } from 'components/Menu/Menu'
import { Favorite } from 'api/clients/FavoriteClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ShareSheet } from 'components/ShareSheet/ShareSheet'
import { FavoriteService } from 'api/services/FavoriteService'
import { ReactComponent as DuplicateIcon } from 'assets/icons/copy16.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit16.svg'
import { ReactComponent as LinkIcon } from 'assets/icons/link16.svg'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings16.svg'
import { ReactComponent as FavoritesIcon } from 'assets/icons/star16.svg'
import { useModals } from 'almanac-ui/Modal'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useSettingsEditable } from 'components/Handbook2_0/ContextMenus/ItemMenu/hooks/useSettingsEditable'
import { ActionKey } from 'components/Handbook2_0/ContextMenus/Types'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { AlmanacFile as File, RootState } from 'types'
import { pluralize } from 'utils'
import { getAlmanacUrlFor } from 'utils/urlUtils'
import Styles from './NavRightClickMenu.module.scss'
import { observer } from 'mobx-react-lite'
import { Unfavorite, ArrowOut, Share, Move, Delete } from 'almanac-ui/icons/16'
import { fileTypeToFavoritableType, findFavorite } from './utils'
import DeleteModal from './components/DeleteModal/DeleteModal'
import { FolderSettingsModal } from 'components/FolderMenu/FolderSettingsModal/FolderSettingsModal'
import { NavRightClickMenuProps } from './Types'

export const NavRightClickMenu = observer(
  ({
    showShare,
    selectedFiles,
    onDeleteRequest,
    disableEditOnlyButtons,
    setShowRename,
    visible = false,
    setVisible,
    showRename = true,
    showMove = true,
    showDuplicate = true,
    showDelete = true,
    onAction,
    disabledKeys,
  }: NavRightClickMenuProps) => {
    const openWindow = useOpenWindowSafely()
    const dispatch = useDispatch()
    const favorites = useSelector(({ favorites }: RootState) => favorites)

    const { openModal: openAlmanacModal } = useModals()
    const { handleSettingsEditable } = useSettingsEditable(selectedFiles[0]?.handbook?.key)

    const defaultFolder = selectedFiles.find((file) => file?.default_project)

    const currentFavorites = selectedFiles.filter((selectedFile) => {
      return findFavorite({ favorites, fileId: selectedFile.id, fileType: selectedFile.type })
    })

    const handleFavorite = useCallback(async () => {
      const file = selectedFiles[0]
      const type = fileTypeToFavoritableType(file.type)
      const favorite = findFavorite({ favorites, fileId: file.id, fileType: file.type })

      if (favorite) {
        const resp = await FavoriteService.deleteFavorite(favorite.id, dispatch)
        if (resp.type === SUCCESS_RESPONSE_TYPE) {
          showToastMessage('1 item remove from Favorites')
        }
      } else {
        const resp = await FavoriteService.createFavorite(file.id, type, dispatch)
        if (resp.type === SUCCESS_RESPONSE_TYPE) {
          showToastMessage('1 item added to Favorites')
        }
      }
    }, [selectedFiles, favorites, dispatch])

    const handleMultipleFavorites = useCallback(async () => {
      let data: { currentFavorites: Favorite[]; nonFavorites: File[] } = { currentFavorites: [], nonFavorites: [] }

      selectedFiles.forEach((selectedFile) => {
        const type = fileTypeToFavoritableType(selectedFile.type)
        const favorite = find(favorites, { favoritable_id: selectedFile.id, favoritable_type: type }) as
          | Favorite
          | undefined
        if (favorite) {
          data.currentFavorites.push(favorite)
        } else {
          data.nonFavorites.push(selectedFile)
        }
      })

      if (data.currentFavorites.length === selectedFiles.length) {
        showToastMessage(
          `${data.currentFavorites.length} ${pluralize('item', data.currentFavorites.length)} added to Favorites`,
        )
        setVisible(false)
        return
      }

      const responses = await Promise.all(
        data.nonFavorites.map((file) => {
          const type = fileTypeToFavoritableType(file.type)
          return FavoriteService.createFavorite(file.id, type, dispatch)
        }),
      )

      const numOfFavorites = responses.reduce((acc, resp) => {
        if (resp.type === SUCCESS_RESPONSE_TYPE) {
          return acc + 1
        }

        return acc
      }, data.currentFavorites.length)

      showToastMessage(`${numOfFavorites} ${pluralize('item', numOfFavorites)} added to Favorites`)
      setVisible(false)
    }, [selectedFiles, favorites, dispatch, setVisible])

    const handleFavorites = useCallback(async () => {
      if (selectedFiles.length === 1) {
        handleFavorite()
      } else {
        handleMultipleFavorites()
      }

      setVisible(false)
    }, [selectedFiles, handleFavorite, handleMultipleFavorites, setVisible])

    const copyToClipboard = useCallback(() => {
      if (!selectedFiles[0].url) {
        showToastMessage('Could not copy link to your clipboard', 'error')
      } else {
        const link = getAlmanacUrlFor(selectedFiles[0].url)
        const copied = copy(link)
        if (copied) {
          showToastMessage('Link copied', 'success')
          return
        }
        showToastMessage('Could not copy link to your clipboard', 'error')
      }

      setVisible(false)
    }, [selectedFiles, setVisible])

    useKey(
      'Escape',
      () => {
        if (visible) {
          setVisible(false)
        }
      },
      { options: true },
      [visible, setVisible],
    )

    const handleAction = (action: Key) => {
      if (action === 'openInNewTab') {
        openWindow(selectedFiles[0].url || '')
      }

      if (action === 'favorites') {
        handleFavorites()
      }

      if (action === 'projectSettings') {
        if (selectedFiles[0]?.handbook) {
          openAlmanacModal(FolderSettingsModal, {
            handbookKey: selectedFiles[0].handbook.key,
            folderTitle: selectedFiles[0].title,
          })
        } else {
          handleSettingsEditable(ActionKey.settings)
        }
        setVisible(false)
      }

      if (action === 'share') {
        const type = selectedFiles[0].type === 'folder' ? 'folder' : 'article'
        openAlmanacModal(ShareSheet, {
          file: {
            id: selectedFiles[0].id,
            type,
          },
        })
        setVisible(false)
      }

      if (action === 'copyLink') {
        copyToClipboard()
      }

      if (action === 'rename') {
        setVisible(false)
        setShowRename(true)
      }

      if (action === 'delete') {
        setVisible(false)
        openAlmanacModal(DeleteModal, {
          files: selectedFiles,
          onRequest: () => onDeleteRequest(selectedFiles),
        })
      }

      onAction?.(action)
    }

    let hiddenKeys = new Set(disabledKeys)
    if (!selectedFiles.length || disableEditOnlyButtons || !!defaultFolder) hiddenKeys.add('move')
    if (disableEditOnlyButtons) {
      hiddenKeys.add('projectSettings')
      hiddenKeys.add('delete')
      hiddenKeys.add('rename')
    }

    return (
      <Menu
        onAction={handleAction}
        aria-label="Document Menu"
        className={Styles.menu}
        itemClassName={Styles.menuItem}
        disabledKeys={hiddenKeys}
      >
        <Section>
          {
            [
              selectedFiles.length === 1 && selectedFiles[0].url && (
                <Item key="openInNewTab" textValue="Open in new tab">
                  <>
                    <ArrowOut /> Open in new tab
                  </>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              <Item key="favorites" textValue="Toggle Favorite">
                <>
                  {currentFavorites.length === selectedFiles.length && selectedFiles.length === 1 ? (
                    <>
                      <Unfavorite /> Remove from Favorites
                    </>
                  ) : (
                    <>
                      <FavoritesIcon /> Add to Favorites
                    </>
                  )}
                </>
              </Item>,

              selectedFiles.length === 1 && selectedFiles[0].type === 'folder' && (
                <Item key="projectSettings" textValue="Project settings">
                  <>
                    <SettingsIcon /> Project settings
                  </>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              showShare && (
                <Item key="share" textValue="Share">
                  <>
                    <Share /> Share
                  </>
                </Item>
              ),
              selectedFiles.length === 1 && (
                <Item key="copyLink" textValue="Copy Link">
                  <>
                    <LinkIcon /> Copy link
                  </>
                </Item>
              ),
              showMove && (
                <Item key="move" textValue="Move">
                  <Tooltip
                    label={`${
                      selectedFiles.length === 1 ? 'This default folder' : defaultFolder?.title
                    } cannot be moved`}
                    placement="right"
                    tooltipState={{ isOpen: !!defaultFolder }}
                  >
                    <span>
                      <Move /> Move
                    </span>
                  </Tooltip>
                </Item>
              ),
              showDuplicate && (
                <Item key="copy" textValue="Make a copy">
                  <>
                    <DuplicateIcon /> Make a copy
                  </>
                </Item>
              ),
              showRename && selectedFiles.length === 1 && (
                <Item key="rename" textValue="Rename">
                  <>
                    <EditIcon /> Rename
                  </>
                </Item>
              ),
              showDelete && (
                <Item key="delete" textValue="Delete">
                  <Tooltip
                    label={`${
                      selectedFiles.length === 1 ? 'This default folder' : defaultFolder?.title
                    } cannot be deleted`}
                    placement="right"
                    tooltipState={{ isOpen: !!defaultFolder }}
                  >
                    <span>
                      <Delete /> Delete
                    </span>
                  </Tooltip>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    )
  },
)
NavRightClickMenu.displayName = 'NavRightClickMenu'
